"use client";

import styled from "styled-components";
import VELYSLogo from "@/assets/logos/VELYSLogo.svg";
import { footerItems, patientFooterItems } from "@/lib/constants/navItems";
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import { useBoundStore } from "@/lib/stores/useBoundStore";
import { Modal } from "../Modal/Modal";
import { ContactUsContent } from "../Modal/ContactUsContent";
import { CopyrightContent } from "../Modal/CopyrightContent";
import { usePathname } from "next/navigation";
import { HealthcareTeamContent } from "../Modal/HealthcareTeamContent";

declare global {
  interface Window {
    OneTrust?: {
      ToggleInfoDisplay: () => void;
    };
  }
}

const StyledFooter = styled.div`
  background-color: ${(props) => props.theme.footer};
  display: flex;
  flex-direction: row;
  padding: 20px 30px;
  gap: 50px;
  justify-content: space-between;
  align-items: flex-start;

  @media (max-width: 1024px) {
    flex-direction: column-reverse;
    gap: 30px;
  }

  .footer-items {
    display: flex;
    row-gap: 10px;
    column-gap: 75px;
    flex-wrap: wrap;

    @media (max-width: 1024px) {
      column-gap: 50px;
    }

    p {
      cursor: pointer;
      color: #fff;
      font-size: 1rem;
      font-style: normal;
      font-weight: 400;
    }
  }
`;

export default function Footer() {
  const t = useTranslations();
  const locale = useLocale();
  const pathname = usePathname();
  const isPatient = pathname.includes("/patient/");
  const userLicenses = useBoundStore((state) => state.userLicenses);
  const userCountry = useBoundStore((state) => state.userCountry);

  // modal specific
  const isModalOpen = useBoundStore((state) => state.isModalOpen);
  const setModalType = useBoundStore((state) => state.setModalType);
  const modalType = useBoundStore((state) => state.modalType);
  const setModalOpen = useBoundStore((state) => state.setModalOpen);
  const handleCloseModal = () => {
    setModalOpen(false);
    setModalType("");
  };

  return (
    <>
      <StyledFooter>
        <Image
          src={VELYSLogo.src}
          alt="velys-enabling-tech-logo"
          width={225}
          height={0}
          style={{ height: "auto" }}
        />
        <div className="footer-items">
          {(isPatient
            ? userLicenses &&
              userLicenses?.["jnj.velys.patientpath.custom"] !== true
              ? patientFooterItems.filter(
                  (item) => item.name !== "healthcare-team"
                )
              : patientFooterItems
            : footerItems
          ).map((item) =>
            item.name === "do-not-sell-share" ? (
              !["GB", "AT", "CH", "DE", "IE", "SG", "MY"].includes(
                userCountry
              ) && (
                <a
                  key={item.name}
                  href="https://privacyportal.onetrust.com/webform/96f23ee1-34e3-41d6-8d5a-07f0d554152b/659417d6-807f-4eb1-87a8-709ce6363e5c?website=velys.jnj.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <p>{t(item.text)}</p>
                </a>
              )
            ) : item.name === "cookie-policy" ? (
              <p
                key={item.name}
                onClick={() => {
                  if (window.OneTrust) {
                    window.OneTrust.ToggleInfoDisplay();
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                {t(item.text)}
              </p>
            ) : item.name === "privacy" ? (
              <a
                key={item.name}
                href={`/${locale}/legal/${
                  isPatient ? "patient-privacy" : "hcp-privacy"
                }`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <p>{t(item.text)}</p>
              </a>
            ) : (
              <p
                key={item.name}
                onClick={() => {
                  setModalOpen(true);
                  setModalType(item.name);
                }}
              >
                {t(item.text)}
              </p>
            )
          )}
        </div>
      </StyledFooter>
      {isModalOpen &&
        (modalType === "contact" || modalType === "product-support") && (
          <Modal
            title={
              modalType === "product-support"
                ? t("modalTitles.productSupport")
                : t("contactUs.title")
            }
            showtimesIcon
            content={<ContactUsContent />}
            toggleModal={handleCloseModal}
          />
        )}
      {isModalOpen && modalType === "copyright" && (
        <Modal
          title={t("copyright.title")}
          showtimesIcon
          content={<CopyrightContent />}
          toggleModal={handleCloseModal}
        />
      )}
      {isModalOpen && modalType === "healthcare-team" && (
        <Modal
          title={t("healthcareTeam.title")}
          showtimesIcon
          content={<HealthcareTeamContent />}
          toggleModal={handleCloseModal}
        />
      )}
    </>
  );
}
